import React, { useMemo } from 'react';
import { Formatting } from '../utils';
import styled from 'styled-components';
import moment from 'moment';
import {
	Client,
	ClientOrder,
	getReadableBdo,
	getReadablePaymentType,
	getReadablePriceType,
	OrderType,
	PriceType,
} from 'types';
import { cyan, gold, green, magenta, purple, red, volcano } from '@ant-design/colors';

const Print = styled.div``;

const PrintTable = styled.table`
	width: 100%;
	page-break-inside: auto;

	tr {
		page-break-inside: avoid;
		page-break-after: auto;

		&:nth-child(2n) {
			background: #eee;
		}

		> td,
		> th {
			padding: 4px 8px;
			text-align: center;
		}

		> th {
			font-size: 14px;

			> div {
				display: flex;
				flex-direction: column;

				> h5 {
					font-weight: 600;
					margin-bottom: 4px;
				}

				> span {
					font-weight: 400;
					font-size: 8px;
				}
			}
		}

		> td {
			font-size: 10px;

			> div {
				display: flex;
				flex-direction: column;

				> h6 {
					font-weight: 600;
					font-size: 12px;
					margin-bottom: 2px;
				}
			}
		}
	}
`;

const PrintTitle = styled.h2`
	width: 100%;
	margin-top: 16px;
	margin-bottom: 8px;
	text-align: center;
	font-size: 18px;
`;

const PrintSubtitle = styled.h3`
	width: 100%;
	margin-top: 16px;
	margin-bottom: 32px;
	text-align: center;
	font-size: 14px;
`;

const DayTitle = styled(PrintSubtitle)`
	margin-top: 48px;
`;

const Summary = styled.h4`
	text-align: center;
	margin: 0;
	font-weight: 400;

	&:first-of-type {
		margin-top: 32px;
	}
`;

const getColor = (order: ClientOrder, type: 'delivery' | 'replacement' | 'claiming') => {
	if (order.type === OrderType.classic) {
		if (order.onPlace) {
			return purple[5];
		}

		switch (type) {
			case 'delivery':
				return green[5];

			case 'replacement':
				return gold[5];

			case 'claiming':
				return red[5];
		}
	}

	if (order.type === OrderType.loose) {
		if (order.onPlace) {
			return magenta[3];
		}

		switch (type) {
			case 'delivery':
				return cyan[4];

			case 'replacement':
				return magenta[3];

			case 'claiming':
				return magenta[3];
		}
	}

	return volcano[5];
};

interface Props {
	client: Client;
	orders: ClientOrder[];
}

const ClientOrdersPrint: React.FC<Props> = ({ client, orders }) => {
	if (!orders) return <div></div>;

	const days = orders.reduce<Record<string, ClientOrder[]>>((acc, order) => {
		const date = order.date;
		const dateString = moment(date).format('DD.MM.YYYY');

		if (!acc[dateString]) acc[dateString] = [];
		acc[dateString].push(order);

		return acc;
	}, {});

	const unpaidSum = useMemo(() => {
		return orders.reduce((acc, order) => {
			const leftToPay = order.grossPrice - order.paid;
			return acc + leftToPay;
		}, 0);
	}, [orders]);

	const paidSum = useMemo(() => {
		return orders.reduce((acc, order) => {
			acc += order.paid;
			return acc;
		}, 0);
	}, [orders]);

	let k = 0;

	return (
		<Print key={client.id} className="print">
			<PrintTitle>
				Wydruk zamówień dla klienta {client.name}
				{client.street && client.city && client.postal && ` - ${client.street}, ${client.city} ${client.postal}`}
			</PrintTitle>
			<PrintSubtitle>Stan z dnia {Formatting.epochToDate(Date.now())}</PrintSubtitle>
			{Object.entries(days).map(([day, orders]) => (
				<React.Fragment key={day}>
					<DayTitle>{day}</DayTitle>
					<PrintTable>
						<tbody>
							<tr>
								<th>
									<div>
										<h5>LP</h5>
										<span>w grupie</span>
									</div>
								</th>
								<th>
									<div>
										<h5>Klient</h5>
										<span>Nazwa</span>
										<span>NIP</span>
										<span>Email</span>
									</div>
								</th>
								<th>
									<div>
										<h5>Cena</h5>
										<span>Brutto / Netto</span>
										<span>Typ płatności</span>
									</div>
								</th>
								<th>
									<div>
										<h5>Adres</h5>
										<span>BDO</span>
										<span>Rodzaj odpadów</span>
									</div>
								</th>
								<th>Faktura</th>
								<th>Data</th>
							</tr>
							{orders.map((order, j) => (
								<tr key={order.id}>
									<td
										style={{
											background: getColor(order, order.wasReplacement ? 'replacement' : 'delivery'),
											minWidth: '100px',
										}}
									>
										<div>
											<h6>{++k}</h6>
											<span>{j + 1}</span>
										</div>
									</td>
									<td>
										<div>
											<span>{client.name}</span>
											<span>{client.nip}</span>
											<span>{client.email}</span>
										</div>
									</td>
									<td>
										<div>
											<h6>{Formatting.money(order.priceType === PriceType.net ? order.netPrice : order.grossPrice)}</h6>
											<span>{getReadablePriceType(order.priceType)}</span>
											<span>{getReadablePaymentType(order.wantedPaymentType)}</span>
										</div>
									</td>
									<td>
										<div>
											<h6>{order.address}</h6>
											<span>{getReadableBdo(order.bdo)}</span>
											<span>{order.wasteType}</span>
										</div>
									</td>
									<td>{order.invoiceDate ?? '-'}</td>
									<td>{Formatting.epochToDate(order.date, true)}</td>
								</tr>
							))}
						</tbody>
					</PrintTable>
				</React.Fragment>
			))}
			<Summary>Opłacone: {Formatting.money(paidSum)}</Summary>
			<Summary>Nieopłacone: {Formatting.money(unpaidSum)}</Summary>
			<Summary>Razem: {Formatting.money(paidSum + unpaidSum)}</Summary>
		</Print>
	);
};

export default class extends React.PureComponent<Props> {
	override render() {
		return <ClientOrdersPrint {...this.props} />;
	}
}
